// src/client/App.tsx
import { useEffect, useRef } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@client/auth/AuthProvider';
import { queryClient } from '@client/lib/queryClient';
import { AppRoutes } from '@client/routes';
import { ToastProvider, useToast } from '@client/components/ToastProvider';
import ErrorBoundary from '@client/components/ErrorBoundary';
import { usePwaUpdate } from '@client/hooks/usePwaUpdate';

/**
 * UpdateNotifier — placed inside <ToastProvider> so useToast() is available.
 * When vite-plugin-pwa signals a new service worker is waiting (needRefresh),
 * we show a brief info toast and reload after 1.5 s to activate the new SW.
 * The handledRef prevents the effect from firing more than once.
 */
function UpdateNotifier(): null {
  const { needRefresh, update } = usePwaUpdate();
  const toast = useToast();
  const handledRef = useRef(false);

  useEffect(() => {
    if (needRefresh && !handledRef.current) {
      handledRef.current = true;
      toast.show('Updating Thandizo…', 'info');
      const timer = setTimeout(update, 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [needRefresh, toast, update]);

  return null;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <ErrorBoundary>
              <UpdateNotifier />
              <AppRoutes />
            </ErrorBoundary>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
